import { FormEvent, useState } from 'react';
import useTableOrder from 'hooks/tableOrder';
import { Supplier } from 'types/supplier';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export type SupplierQueryParams = {
  active: boolean;
  company_name: string;
  document_number: string;
};

export function useFetchSuppliers() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [orderedIndex, sort] = useTableOrder();
  const [filter, setFilter] = useState<SupplierQueryParams>({
    document_number: '',
    company_name: '',
    active: true,
  });

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter) {
    setLoading(true);

    api
      .get<Paginated<Supplier[]>>('/suppliers', {
        params: {
          ...nextFilter,
          active: nextFilter.active ? 1 : 0,
          page: nextPage + 1,
          rows: nextRows,
        },
      })
      .then(response => {
        setTotal(response.data.total);
        const _suppliers = response.data.items.map(supplier => ({
          ...supplier,
          formattedActive: supplier.active ? 'Sim' : 'Não',
          formattedCity: supplier.address?.city || 'Não informado',
          formattedState: supplier.address?.state || 'Não informado',
          formattedStreet: supplier.address?.street || 'Não informado',
          formattedCreatedAt: format(supplier.created_at || new Date(), 'P', { locale: ptBR }),
        }));
        setSuppliers(_suppliers);
      })
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const p = sort(index, suppliers);
    setSuppliers(p);
  }

  function handleChange<K extends keyof SupplierQueryParams>(index: K, value: SupplierQueryParams[K]) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  return {
    suppliers,
    loading,
    orderedIndex,
    handleSort,
    filter,
    handleChange,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    page,
    rows,
    total,
  };
}
